import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import SessionTimeout from '../../components/SessionTimeout';
import api from '../../api/axios';

const C = {
  green: '#3cb74e', black: '#111111', white: '#ffffff',
  red: '#ef4444', amber: '#f59e0b', gray: '#64748b',
  border: '#e2e8f0', lightBg: '#f8fdf8', purple: '#8b5cf6',
};
const statusColors = {
  valid:   { bg: '#dcfce7', text: '#166534' },
  invalid: { bg: '#fee2e2', text: '#991b1b' },
  risky:   { bg: '#fef3c7', text: '#92400e' },
  unknown: { bg: '#f1f5f9', text: '#475569' },
};

export default function AdminDashboard() {
  const { user, logout }                = useAuth();
  const navigate                        = useNavigate();
  const [tab, setTab]                   = useState('dashboard');
  const [stats, setStats]               = useState(null);
  const [clients, setClients]           = useState([]);
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedClients, setSelected]  = useState([]);
  const [clientHistory, setClientHist]  = useState([]);
  const [clientHistPage, setHistPage]   = useState(1);
  const [clientHistTotal, setHistTotal] = useState(0);
  const [viewingClient, setViewClient]  = useState(null);
  const [activityLog, setActivity]      = useState([]);
  const [showForm, setShowForm]         = useState(false);
  const [editClient, setEditClient]     = useState(null);
  const [form, setForm]                 = useState({ name: '', phone: '', email: '', company_name: '', password: '', email_limit: 1000, status: 'active' });
  const [topupClient, setTopupClient]   = useState(null);
  const [topupAmount, setTopupAmount]   = useState('');
  const [regLink, setRegLink]           = useState('');
  const [error, setError]               = useState('');
  const [success, setSuccess]           = useState('');
  const [loading, setLoading]           = useState(false);
  const debounceRef                     = useRef(null);

  useEffect(() => { fetchDashboard(); }, []);
  useEffect(() => {
    if (tab === 'clients') {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(fetchClients, 400);
    }
    if (tab === 'activity') fetchActivity();
  }, [tab, search, statusFilter]);

  const fetchDashboard = async () => { try { const r = await api.get('/admin/dashboard'); setStats(r.data); } catch {} };
  const fetchClients   = async () => { try { const r = await api.get(`/admin/clients?search=${search}&status=${statusFilter}`); setClients(r.data.clients); } catch {} };
  const fetchActivity  = async () => { try { const r = await api.get('/admin/activity-log'); setActivity(r.data.data); } catch {} };

  const fetchClientHistory = async (clientId, page = 1) => {
    try {
      const r = await api.get(`/admin/clients/${clientId}/history?page=${page}`);
      setClientHist(r.data.data);
      setHistTotal(r.data.pagination?.total || 0);
      setHistPage(page);
    } catch {}
  };

  const handleCreateOrUpdate = async () => {
    setError(''); setSuccess('');
    if (!form.name || !form.phone || !form.email || !form.company_name || (!editClient && !form.password))
      return setError('All fields required');
    if (!/^\d{10}$/.test(form.phone)) return setError('Phone must be 10 digits');
    setLoading(true);
    try {
      if (editClient) { await api.put(`/admin/clients/${editClient.id}`, form); setSuccess('Client updated!'); }
      else { await api.post('/admin/clients', form); setSuccess('Client created!'); }
      setShowForm(false); setEditClient(null);
      setForm({ name: '', phone: '', email: '', company_name: '', password: '', email_limit: 1000, status: 'active' });
      fetchClients(); fetchDashboard();
    } catch (err) { setError(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this client? All data will be removed.')) return;
    try { await api.delete(`/admin/clients/${id}`); fetchClients(); fetchDashboard(); } catch {}
  };

  const handleTopup = async () => {
    if (!topupAmount || topupAmount <= 0) return;
    try {
      await api.post(`/admin/clients/${topupClient.id}/topup`, { credits: parseInt(topupAmount) });
      setTopupClient(null); setTopupAmount(''); fetchClients();
      setSuccess(`${topupAmount} credits added to ${topupClient.name}`);
    } catch {}
  };

  const handleBulkLimit = async () => {
    const newLimit = prompt('Enter new email limit for selected clients:');
    if (!newLimit) return;
    const placeholders = selectedClients.map(() => '?').join(',');
    try {
      await api.post('/admin/clients/bulk-limit', { client_ids: selectedClients, email_limit: parseInt(newLimit) });
      setSelected([]); fetchClients(); setSuccess(`Updated ${selectedClients.length} clients`);
    } catch {}
  };

  const handleGenRegLink = async () => {
    try { const r = await api.post('/admin/reg-link'); setRegLink(r.data.link); } catch {}
  };

  const viewClientHistory = (client) => {
    setViewClient(client);
    fetchClientHistory(client.id, 1);
    setTab('clientHistory');
  };

  const openEdit = (client) => {
    setEditClient(client);
    setForm({ name: client.name, phone: client.phone, email: client.email, company_name: client.company_name, password: '', email_limit: client.email_limit, status: client.status });
    setShowForm(true); setTab('clients');
  };

  const navItems = [
    { key: 'dashboard', icon: '📊', label: 'Dashboard' },
    { key: 'clients',   icon: '👥', label: 'Clients' },
    { key: 'addclient', icon: '➕', label: 'Add Client' },
    { key: 'activity',  icon: '📝', label: 'Activity Log' },
  ];

  return (
    <div style={s.page}>
      <SessionTimeout />

      {/* Sidebar */}
      <div style={s.sidebar}>
        <div style={s.brand}>
          <span style={{ fontSize: 28 }}>✉️</span>
          <div>
            <p style={s.brandName}>EmailVerifier</p>
            <p style={s.brandSub}>Admin Panel</p>
          </div>
        </div>
        <div style={s.adminBadge}>🔐 {user?.name}</div>
        <nav style={s.nav}>
          {navItems.map(({ key, icon, label }) => (
            <button key={key}
              style={{ ...s.navBtn, ...((tab === key || (tab === 'clientHistory' && key === 'clients')) ? s.navBtnActive : {}) }}
              onClick={() => {
                if (key === 'addclient') {
                  setShowForm(true); setEditClient(null);
                  setForm({ name: '', phone: '', email: '', company_name: '', password: '', email_limit: 1000, status: 'active' });
                  setTab('clients');
                } else setTab(key);
              }}>
              <span>{icon}</span><span>{label}</span>
            </button>
          ))}
        </nav>
        <button style={s.logoutBtn} onClick={() => { logout(); navigate('/login'); }}>🚪 Logout</button>
      </div>

      {/* Main */}
      <div style={s.main}>
        {success && <div style={s.successBar} onClick={() => setSuccess('')}>✅ {success} ×</div>}
        {error   && <div style={s.errorBar}   onClick={() => setError('')}>⚠️ {error} ×</div>}

        {/* DASHBOARD */}
        {tab === 'dashboard' && stats && (
          <div>
            <h1 style={s.pageTitle}>Dashboard Overview</h1>
            <p style={s.pageSub}>Welcome back, {user?.name} · Today's verifications: <strong style={{ color: C.green }}>{stats.stats?.today_count || 0}</strong></p>

            {/* Alert banner for near-limit clients */}
            {stats.alert_clients?.length > 0 && (
              <div style={s.alertBanner}>
                <span style={s.alertIcon}>⚠️</span>
                <div>
                  <p style={s.alertTitle}>{stats.alert_clients.length} client(s) are near their email limit!</p>
                  <div style={s.alertClients}>
                    {stats.alert_clients.map(c => (
                      <span key={c.id} style={{ ...s.alertBadge, background: c.usage_pct >= 95 ? '#fee2e2' : '#fef3c7', color: c.usage_pct >= 95 ? '#991b1b' : '#92400e' }}>
                        {c.usage_pct >= 95 ? '🚨' : '⚠️'} {c.name} — {c.usage_pct}%
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div style={s.statsGrid}>
              {[
                { icon: '👥', label: 'Total Clients',   value: stats.stats?.total_clients,      color: C.black },
                { icon: '✅', label: 'Active Clients',   value: stats.stats?.active_clients,     color: C.green },
                { icon: '🚫', label: 'Suspended',        value: stats.stats?.suspended_clients,  color: C.red },
                { icon: '⚠️', label: 'Near Limit',       value: stats.stats?.near_limit,         color: C.amber },
                { icon: '📧', label: 'Total Verified',   value: stats.stats?.total_verifications,color: C.black },
                { icon: '✉️', label: 'Valid Emails',     value: stats.stats?.valid_count,        color: C.green },
                { icon: '❌', label: 'Invalid Emails',   value: stats.stats?.invalid_count,      color: C.red },
                { icon: '📅', label: 'Today',            value: stats.stats?.today_count,        color: C.black },
              ].map(({ icon, label, value, color }) => (
                <div key={label} style={s.statCard}>
                  <p style={s.statIcon}>{icon}</p>
                  <p style={{ ...s.statNum, color }}>{value || 0}</p>
                  <p style={s.statLabel}>{label}</p>
                </div>
              ))}
            </div>

            <div style={s.dashGrid}>
              {stats.chart_data?.length > 0 && (
                <div style={s.dashCard}>
                  <h3 style={s.cardTitle}>📈 Verifications (Last 14 Days)</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={stats.chart_data}>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: C.gray }} />
                      <YAxis tick={{ fontSize: 10, fill: C.gray }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="total" stroke={C.green} strokeWidth={2.5} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
              <div style={s.dashCard}>
                <h3 style={s.cardTitle}>🏆 Top Active Clients</h3>
                {stats.top_clients?.map((c, i) => (
                  <div key={c.id} style={s.topClientRow}>
                    <span style={s.topRank}>{i + 1}</span>
                    <div style={{ flex: 1 }}>
                      <p style={s.topName}>{c.name}</p>
                      <p style={s.topCompany}>{c.company_name}</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ color: C.green, fontWeight: 700, fontSize: 14 }}>{c.emails_used}</p>
                      <p style={s.topToday}>+{c.today} today</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={s.dashCard}>
              <h3 style={s.cardTitle}>🕐 Recent Verifications</h3>
              <table style={s.table}>
                <thead>
                  <tr style={s.theadRow}>
                    {['Email', 'Client', 'Company', 'Status', 'Time'].map(h => <th key={h} style={s.th}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_activity?.map((a, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? C.white : C.lightBg }}>
                      <td style={s.td}>{a.email}</td>
                      <td style={s.td}>{a.client_name}</td>
                      <td style={s.td}>{a.company_name}</td>
                      <td style={s.td}><span style={{ ...s.badge, ...statusColors[a.status] }}>{a.status}</span></td>
                      <td style={s.td}>{new Date(a.verified_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* CLIENTS TAB */}
        {tab === 'clients' && !showForm && (
          <div>
            <div style={s.tableHeader}>
              <h1 style={s.pageTitle}>All Clients</h1>
              <div style={s.tableActions}>
                {selectedClients.length > 0 && (
                  <button style={s.bulkBtn} onClick={handleBulkLimit}>⚡ Update Limit ({selectedClients.length})</button>
                )}
                <button style={s.addBtn} onClick={() => { setShowForm(true); setEditClient(null); setForm({ name: '', phone: '', email: '', company_name: '', password: '', email_limit: 1000, status: 'active' }); }}>➕ Add Client</button>
                <button style={s.linkBtn} onClick={handleGenRegLink}>🔗 Reg Link</button>
              </div>
            </div>

            {regLink && (
              <div style={s.regLinkBox}>
                <p style={s.regLinkLabel}>📋 Registration Link (valid 7 days):</p>
                <div style={s.regLinkRow}>
                  <input style={s.regLinkInput} value={regLink} readOnly />
                  <button style={s.copyBtn} onClick={() => { navigator.clipboard.writeText(regLink); setSuccess('Link copied!'); }}>Copy</button>
                </div>
              </div>
            )}

            <div style={s.searchRow}>
              <input style={s.searchInput} placeholder="🔍 Search by name, phone, email, company..." value={search} onChange={e => setSearch(e.target.value)} />
              <select style={s.filterSelect} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>

            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr style={s.theadRow}>
                    <th style={s.th}><input type="checkbox" onChange={e => setSelected(e.target.checked ? clients.map(c => c.id) : [])} /></th>
                    {['Name & Company', 'Phone', 'Email', 'Usage', 'Today', 'Last Active', 'Status', 'Actions'].map(h => <th key={h} style={s.th}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {clients.map((c, i) => {
                    const total = c.email_limit + (c.credits_topup || 0);
                    const pct   = Math.min(((c.emails_used / Math.max(total, 1)) * 100), 100).toFixed(0);
                    const remaining = total - c.emails_used;
                    const isWarn = pct >= 80;
                    const isCrit = pct >= 95;
                    return (
                      <tr key={c.id} style={{ background: i % 2 === 0 ? C.white : C.lightBg }}>
                        <td style={s.td}><input type="checkbox" checked={selectedClients.includes(c.id)} onChange={e => setSelected(e.target.checked ? [...selectedClients, c.id] : selectedClients.filter(id => id !== c.id))} /></td>
                        <td style={s.td}>
                          <p style={s.clientName}>{c.name}</p>
                          <p style={s.clientSub}>{c.company_name}</p>
                        </td>
                        <td style={s.td}>{c.phone}</td>
                        <td style={s.td} title={c.email}>{c.email.length > 20 ? c.email.slice(0,18)+'...' : c.email}</td>
                        <td style={s.td}>
                          <div style={s.usageWrap}>
                            <div style={s.usageTrack}>
                              <div style={{ ...s.usageFill, width: `${pct}%`, background: isCrit ? C.red : isWarn ? C.amber : C.green }} />
                            </div>
                            <span style={{ ...s.usagePct, color: isCrit ? C.red : isWarn ? C.amber : C.gray }}>
                              {c.emails_used}/{total}
                              {isCrit && <span style={{ color: C.red, fontWeight: 700 }}> 🚨</span>}
                              {isWarn && !isCrit && <span style={{ color: C.amber }}> ⚠️</span>}
                            </span>
                            {remaining <= 0 && <span style={{ display: 'block', color: C.red, fontSize: 10, fontWeight: 700 }}>Limit reached</span>}
                          </div>
                        </td>
                        <td style={s.td}><span style={{ color: C.green, fontWeight: 600 }}>{c.today_count || 0}</span></td>
                        <td style={s.td}>{c.last_active ? new Date(c.last_active).toLocaleDateString() : '—'}</td>
                        <td style={s.td}>
                          <span style={{ ...s.badge, background: c.status === 'active' ? '#dcfce7' : c.status === 'suspended' ? '#fee2e2' : '#f1f5f9', color: c.status === 'active' ? '#166534' : c.status === 'suspended' ? '#991b1b' : '#475569' }}>
                            {c.status}
                          </span>
                        </td>
                        <td style={s.td}>
                          <div style={s.actionBtns}>
                            <button style={s.iconBtn} title="Edit"    onClick={() => openEdit(c)}>✏️</button>
                            <button style={s.iconBtn} title="History" onClick={() => viewClientHistory(c)}>📋</button>
                            <button style={s.iconBtn} title="Top-up"  onClick={() => setTopupClient(c)}>💳</button>
                            <button style={{ ...s.iconBtn, background: '#fee2e2' }} title="Delete" onClick={() => handleDelete(c.id)}>🗑️</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {clients.length === 0 && <p style={s.empty}>No clients found.</p>}
            </div>
          </div>
        )}

        {/* CREATE / EDIT FORM */}
        {tab === 'clients' && showForm && (
          <div>
            <div style={s.tableHeader}>
              <h1 style={s.pageTitle}>{editClient ? '✏️ Edit Client' : '➕ Add New Client'}</h1>
              <button style={s.backBtn} onClick={() => { setShowForm(false); setEditClient(null); }}>← Back</button>
            </div>
            {error   && <div style={s.errorBar}>{error}</div>}
            {success && <div style={s.successBar}>{success}</div>}
            <div style={s.formCard}>
              <div style={s.formGrid}>
                {[
                  { name: 'name',         label: 'Full Name *',                              placeholder: 'John Doe',        type: 'text' },
                  { name: 'phone',        label: 'Phone Number * (10 digits)',                placeholder: '9876543210',      type: 'tel' },
                  { name: 'email',        label: 'Email Address *',                          placeholder: 'john@company.com',type: 'email' },
                  { name: 'company_name', label: 'Company Name *',                           placeholder: 'Acme Corp',       type: 'text' },
                  { name: 'password',     label: editClient ? 'New Password (blank = no change)' : 'Password *', placeholder: 'Min 6 characters', type: 'password' },
                  { name: 'email_limit',  label: 'Email Verification Limit',                 placeholder: '1000',            type: 'number' },
                ].map(({ name, label, placeholder, type }) => (
                  <div key={name} style={s.formGroup}>
                    <label style={s.label}>{label}</label>
                    <input style={s.formInput} name={name} type={type} placeholder={placeholder} value={form[name]} onChange={e => setForm({ ...form, [e.target.name]: e.target.value })} maxLength={name === 'phone' ? 10 : undefined} />
                  </div>
                ))}
                {editClient && (
                  <div style={s.formGroup}>
                    <label style={s.label}>Status</label>
                    <select style={s.formInput} name="status" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>
                )}
              </div>
              <button style={s.submitBtn} onClick={handleCreateOrUpdate} disabled={loading}>
                {loading ? '⏳ Saving...' : editClient ? '💾 Update Client' : '✅ Create Client'}
              </button>
            </div>
          </div>
        )}

        {/* CLIENT HISTORY */}
        {tab === 'clientHistory' && viewingClient && (
          <div>
            <div style={s.tableHeader}>
              <div>
                <h1 style={s.pageTitle}>📋 {viewingClient.name}'s History</h1>
                <p style={s.pageSub}>{viewingClient.company_name} · {viewingClient.email}</p>
              </div>
              <button style={s.backBtn} onClick={() => { setTab('clients'); setViewClient(null); }}>← Back</button>
            </div>
            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead><tr style={s.theadRow}>{['Email','Format','MX','SMTP','Catch-all','Disposable','Status','Date'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {clientHistory.map((r, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? C.white : C.lightBg }}>
                      <td style={s.td}>{r.email}</td>
                      <td style={{ ...s.td, textAlign: 'center' }}>{r.format_valid ? '✅' : '❌'}</td>
                      <td style={{ ...s.td, textAlign: 'center' }}>{r.mx_valid ? '✅' : '❌'}</td>
                      <td style={{ ...s.td, textAlign: 'center' }}>{r.smtp_valid ? '✅' : '❌'}</td>
                      <td style={{ ...s.td, textAlign: 'center' }}>{r.is_catchall ? '⚠️' : '—'}</td>
                      <td style={{ ...s.td, textAlign: 'center' }}>{r.is_disposable ? '⚠️' : '—'}</td>
                      <td style={s.td}><span style={{ ...s.badge, ...statusColors[r.status] }}>{r.status}</span></td>
                      <td style={s.td}>{new Date(r.verified_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {clientHistory.length === 0 && <p style={s.empty}>No verifications found.</p>}
            </div>
            {clientHistTotal > 20 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 12, padding: 16 }}>
                <button style={s.backBtn} disabled={clientHistPage === 1} onClick={() => fetchClientHistory(viewingClient.id, clientHistPage - 1)}>← Prev</button>
                <span style={{ color: C.gray, fontSize: 13, alignSelf: 'center' }}>Page {clientHistPage} of {Math.ceil(clientHistTotal / 20)}</span>
                <button style={s.backBtn} disabled={clientHistPage >= Math.ceil(clientHistTotal / 20)} onClick={() => fetchClientHistory(viewingClient.id, clientHistPage + 1)}>Next →</button>
              </div>
            )}
          </div>
        )}

        {/* ACTIVITY LOG */}
        {tab === 'activity' && (
          <div>
            <h1 style={s.pageTitle}>📝 Admin Activity Log</h1>
            <p style={s.pageSub}>Track all admin actions</p>
            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead><tr style={s.theadRow}>{['Action','Target','Details','Date & Time'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {activityLog.map((a, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? C.white : C.lightBg }}>
                      <td style={s.td}><span style={{ ...s.badge, background: '#f0fdf4', color: C.green }}>{a.action}</span></td>
                      <td style={s.td}>{a.target_type} {a.target_id ? `#${a.target_id}` : ''}</td>
                      <td style={s.td}>{a.details}</td>
                      <td style={s.td}>{new Date(a.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {activityLog.length === 0 && <p style={s.empty}>No activity logged yet.</p>}
            </div>
          </div>
        )}
      </div>

      {/* Top-up Modal */}
      {topupClient && (
        <div style={s.modalOverlay}>
          <div style={s.modal}>
            <h3 style={s.modalTitle}>💳 Add Credits — {topupClient.name}</h3>
            <p style={s.modalSub}>Current: {topupClient.emails_used}/{topupClient.email_limit + (topupClient.credits_topup||0)}</p>
            <input style={s.formInput} type="number" placeholder="Credits to add (e.g. 500)" value={topupAmount} onChange={e => setTopupAmount(e.target.value)} />
            <div style={s.modalBtns}>
              <button style={s.submitBtn} onClick={handleTopup}>✅ Add Credits</button>
              <button style={s.cancelBtn} onClick={() => { setTopupClient(null); setTopupAmount(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page: { display: 'flex', minHeight: '100vh', background: '#f8fdf8' },
  sidebar: { width: 220, background: C.black, padding: '20px 14px', display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 },
  brand: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid rgba(255,255,255,0.1)' },
  brandName: { color: C.green, fontWeight: 800, fontSize: 15, margin: 0 },
  brandSub: { color: 'rgba(255,255,255,0.4)', fontSize: 10, margin: 0 },
  adminBadge: { background: 'rgba(60,183,78,0.15)', color: C.green, borderRadius: 8, padding: '8px 12px', fontSize: 12, fontWeight: 600, marginBottom: 10, border: '1px solid rgba(60,183,78,0.3)' },
  nav: { display: 'flex', flexDirection: 'column', gap: 2, flex: 1 },
  navBtn: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, border: 'none', background: 'transparent', color: 'rgba(255,255,255,0.55)', cursor: 'pointer', textAlign: 'left', fontSize: 13, fontWeight: 500 },
  navBtnActive: { background: C.green, color: C.white, fontWeight: 700 },
  logoutBtn: { padding: '10px 12px', borderRadius: 8, border: 'none', background: 'rgba(239,68,68,0.15)', color: '#fca5a5', cursor: 'pointer', textAlign: 'left', fontSize: 13, fontWeight: 600, marginTop: 8 },
  main: { flex: 1, padding: 28, overflowY: 'auto' },
  pageTitle: { color: C.black, fontSize: 22, fontWeight: 800, margin: '0 0 4px' },
  pageSub: { color: C.gray, fontSize: 13, margin: '0 0 20px' },
  alertBanner: { display: 'flex', gap: 14, background: '#fef3c7', border: '1.5px solid #fde68a', borderRadius: 12, padding: '14px 18px', marginBottom: 20, alignItems: 'flex-start' },
  alertIcon: { fontSize: 22 },
  alertTitle: { color: '#92400e', fontWeight: 700, fontSize: 14, margin: '0 0 8px' },
  alertClients: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  alertBadge: { padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600 },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 14, marginBottom: 20 },
  statCard: { background: C.white, border: `1.5px solid ${C.border}`, borderRadius: 12, padding: '16px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  statIcon: { fontSize: 24, margin: '0 0 8px' },
  statNum: { fontSize: 28, fontWeight: 800, margin: 0 },
  statLabel: { color: C.gray, fontSize: 11, margin: '4px 0 0' },
  dashGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 },
  dashCard: { background: C.white, border: `1.5px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  cardTitle: { color: C.black, fontSize: 15, fontWeight: 700, margin: '0 0 14px' },
  topClientRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: `1px solid ${C.border}` },
  topRank: { width: 24, height: 24, background: '#f0fdf4', color: C.green, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 },
  topName: { color: C.black, fontWeight: 600, fontSize: 13, margin: 0 },
  topCompany: { color: C.gray, fontSize: 11, margin: 0 },
  topToday: { color: C.green, fontSize: 11 },
  tableHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  tableActions: { display: 'flex', gap: 8 },
  addBtn: { padding: '9px 18px', background: C.green, color: C.white, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 },
  bulkBtn: { padding: '9px 18px', background: C.purple, color: C.white, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 },
  linkBtn: { padding: '9px 18px', background: C.black, color: C.white, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 },
  backBtn: { padding: '9px 18px', background: '#f1f5f9', color: C.black, border: `1.5px solid ${C.border}`, borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  regLinkBox: { background: '#f0fdf4', border: `1.5px solid ${C.border}`, borderRadius: 10, padding: 14, marginBottom: 16 },
  regLinkLabel: { color: C.black, fontSize: 13, fontWeight: 600, margin: '0 0 8px' },
  regLinkRow: { display: 'flex', gap: 8 },
  regLinkInput: { flex: 1, padding: '8px 12px', border: `1.5px solid ${C.border}`, borderRadius: 6, fontSize: 12, color: C.gray, background: C.white },
  copyBtn: { padding: '8px 14px', background: C.green, color: C.white, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  searchRow: { display: 'flex', gap: 10, marginBottom: 14 },
  searchInput: { flex: 1, padding: '10px 14px', border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 14, outline: 'none', color: C.black, background: C.white },
  filterSelect: { padding: '10px 14px', border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 13, outline: 'none', background: C.white, color: C.black },
  tableWrap: { background: C.white, borderRadius: 12, border: `1.5px solid ${C.border}`, overflow: 'auto', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  theadRow: { background: C.black },
  th: { padding: '11px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: C.white, whiteSpace: 'nowrap' },
  td: { padding: '11px 14px', color: C.black, fontSize: 13, borderBottom: `1px solid ${C.border}` },
  badge: { padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600 },
  clientName: { color: C.black, fontWeight: 700, fontSize: 13, margin: 0 },
  clientSub: { color: C.gray, fontSize: 11, margin: 0 },
  usageWrap: { minWidth: 130 },
  usageTrack: { height: 5, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden', marginBottom: 3 },
  usageFill: { height: '100%', borderRadius: 3 },
  usagePct: { fontSize: 11, fontWeight: 600 },
  actionBtns: { display: 'flex', gap: 4 },
  iconBtn: { background: '#f0fdf4', border: 'none', borderRadius: 6, padding: '5px 8px', cursor: 'pointer', fontSize: 13 },
  empty: { textAlign: 'center', padding: 40, color: C.gray },
  formCard: { background: C.white, borderRadius: 12, border: `1.5px solid ${C.border}`, padding: 24, maxWidth: 700, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 },
  formGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { color: C.black, fontSize: 12, fontWeight: 700 },
  formInput: { padding: '10px 14px', border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 14, outline: 'none', color: C.black, background: C.white },
  submitBtn: { padding: '11px 28px', background: C.green, color: C.white, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 14 },
  cancelBtn: { padding: '11px 28px', background: '#f1f5f9', color: C.black, border: `1.5px solid ${C.border}`, borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 },
  successBar: { background: '#f0fdf4', border: '1.5px solid #86efac', color: '#166534', padding: '10px 16px', borderRadius: 8, fontSize: 13, marginBottom: 16, cursor: 'pointer' },
  errorBar: { background: '#fef2f2', border: '1.5px solid #fca5a5', color: '#991b1b', padding: '10px 16px', borderRadius: 8, fontSize: 13, marginBottom: 16, cursor: 'pointer' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 },
  modal: { background: C.white, borderRadius: 16, padding: 28, width: 380, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' },
  modalTitle: { color: C.black, fontSize: 18, fontWeight: 700, margin: '0 0 6px' },
  modalSub: { color: C.gray, fontSize: 13, margin: '0 0 16px' },
  modalBtns: { display: 'flex', gap: 10, marginTop: 16 },
};
